/**
 * Design tokens for the shared UI system
 * Inspired by Park UI's token-based approach
 */

export const tokens = {
  // Colors
  colors: {
    // Brand colors
    brand: {
      primary: "#E57B2C",
      secondary: "#FFF7E6",
      accent: "#c85a3a",
      deep: "#4a3a2a",
      highlight: "#f4e4b8",
    },
    // Neutral colors
    neutral: {
      50: "#fafafa",
      100: "#f5f5f5",
      200: "#e5e5e5",
      300: "#d4d4d4",
      400: "#a3a3a3",
      500: "#737373",
      600: "#525252",
      700: "#404040",
      800: "#262626",
      900: "#171717",
    },
    // Semantic colors
    semantic: {
      success: "#22c55e",
      warning: "#f59e0b",
      error: "#ef4444",
      info: "#3b82f6",
    },
    // Intent palettes for status/alert UI
    intent: {
      error: {
        bg: "#fef2f2",
        border: "#fecaca",
        text: "#dc2626",
        textStrong: "#b91c1c",
      },
      warning: {
        bg: "#fef3c7",
        border: "#fde68a",
        text: "#92400e",
      },
      success: {
        bg: "#dcfce7",
        border: "#86efac",
        text: "#166534",
      },
      info: {
        bg: "#dbeafe",
        border: "#93c5fd",
        text: "#1e40af",
      },
    },
    // Background colors
    background: {
      primary: "#f5f0e8",
      secondary: "#fef7e0",
      tertiary: "#FFF7E6",
    },
    surfaces: {
      eventHighlight: "#f4e4b8",
      bringSection: "#f0f8ff",
      infoSection: "#f8f9fa",
      readingList: "#fff6eb",
      gallery: "#fdfaf5",
      card: "#ffffff",
      storytimeSunny: "#fef8e6",
      storytimeGlow: "#fdeccf",
      storytimeAmber: "#fcdcb3",
      tech: "#dbeafe",
      techAccent: "#bfdbfe",
      techDeep: "#93c5fd",
    },
    status: {
      current: "#27ae60",
      future: "#3498db",
      past: "#95a5a6",
      default: "#7f8c8d",
      closed: "#e74c3c",
    },
    border: {
      subtle: "rgba(0, 0, 0, 0.05)",
    },
    overlay: {
      warm: "rgba(255, 255, 255, 0.5)",
      backdrop: "rgba(17, 24, 39, 0.65)",
    },
    admin: {
      bgPrimary: "#0f172a",
      bgSecondary: "#1e293b",
      bgTertiary: "#334155",
    },
  },

  // Typography
  typography: {
    fontFamily: {
      sans: "'Poppins', 'Nunito', 'Comic Neue', system-ui, -apple-system, sans-serif",
      heading: "'Fredoka One', 'Bubblegum Sans', 'Poppins', cursive",
      mono: "ui-monospace, SFMono-Regular, 'SF Mono', monospace",
    },
    fontSize: {
      xs: "0.75rem",
      sm: "0.875rem",
      base: "1rem",
      lg: "1.125rem",
      xl: "1.25rem",
      "2xl": "1.5rem",
      "3xl": "1.875rem",
      "4xl": "2.25rem",
      "5xl": "3rem",
    },
    fontWeight: {
      normal: "400",
      medium: "500",
      semibold: "600",
      bold: "700",
    },
    lineHeight: {
      tight: "1.25",
      normal: "1.5",
      relaxed: "1.75",
    },
  },

  // Spacing
  spacing: {
    0: "0",
    1: "0.25rem",
    2: "0.5rem",
    3: "0.75rem",
    4: "1rem",
    5: "1.25rem",
    6: "1.5rem",
    8: "2rem",
    10: "2.5rem",
    12: "3rem",
    16: "4rem",
    20: "5rem",
    24: "6rem",
  },

  // Border radius
  borderRadius: {
    none: "0",
    sm: "0.125rem",
    base: "0.25rem",
    md: "0.375rem",
    lg: "0.5rem",
    xl: "0.75rem",
    "2xl": "1rem",
    "3xl": "1.5rem",
    full: "9999px",
  },

  // Shadows
  boxShadow: {
    sm: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    base: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
    md: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
    lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
    xl: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
    subtle: "0 1px 2px rgba(0, 0, 0, 0.04)",
  },

  // Breakpoints
  breakpoints: {
    sm: "640px",
    md: "768px",
    lg: "1024px",
    xl: "1280px",
    "2xl": "1536px",
  },
} as const;

export type Tokens = typeof tokens;
